import { useState, useEffect } from "react";

interface OfficeContextState {
  isReady: boolean;
  userEmail: string | null;
  error: string | null;
  /**
   * True when the page is loaded outside of Outlook (e.g. opened directly in a
   * web browser). In that case we show a friendly informational landing page
   * instead of an error, so the URL always resolves to a functioning website.
   */
  isOutsideOffice: boolean;
}

/**
 * Hook to initialize Office.js and get user context.
 * Safe to call even after Office is already initialized (callback fires immediately).
 */
export function useOfficeContext(): OfficeContextState {
  const [state, setState] = useState<OfficeContextState>({
    isReady: false,
    userEmail: null,
    error: null,
    isOutsideOffice: false,
  });

  useEffect(() => {
    let settled = false;

    const markOutsideOffice = () => {
      if (settled) return;
      settled = true;
      setState({
        isReady: false,
        userEmail: null,
        error: null,
        isOutsideOffice: true,
      });
    };

    // Fallback: if Office.js never signals readiness (e.g. the page is opened
    // directly in a browser and onReady does not fire), treat it as "outside
    // Office" after a short delay so we render the landing page rather than a
    // perpetual spinner.
    const fallbackTimer = window.setTimeout(markOutsideOffice, 3000);

    try {
      Office.onReady((info) => {
        if (settled) return;

        // Not running inside Outlook (opened in a browser, or another host).
        if (!info || info.host !== Office.HostType.Outlook) {
          markOutsideOffice();
          return;
        }

        settled = true;
        window.clearTimeout(fallbackTimer);

        const mailbox = Office.context.mailbox;
        const userEmail = mailbox?.userProfile?.emailAddress;

        if (!userEmail) {
          setState({
            isReady: false,
            userEmail: null,
            error: "Unable to get your Outlook email. Please reopen the add-in.",
            isOutsideOffice: false,
          });
          return;
        }

        setState({
          isReady: true,
          userEmail: userEmail.toLowerCase(),
          error: null,
          isOutsideOffice: false,
        });
      });
    } catch {
      // Office.js not available at all -> render the landing page.
      markOutsideOffice();
    }

    return () => window.clearTimeout(fallbackTimer);
  }, []);

  return state;
}
